import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  Refresh,
  Person,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import apiClient from '@/services/apiClient';
import toast from 'react-hot-toast';

interface PendingDisplayName {
  userId: number;
  legalName: string;
  displayName: string;
  requestedAt: string;
  email: string;
  phoneNumber: string;
  listingCount: number;
}

const DisplayNameReviewPage: React.FC = () => {
  const [pending, setPending] = useState<PendingDisplayName[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDisplayName, setSelectedDisplayName] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingDisplayNames();
  }, []);

  const fetchPendingDisplayNames = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/v1/admin/display-names/pending');

      if (response.data.success) {
        setPending(response.data.data.pending || []);
      } else {
        toast.error('Failed to load pending display names');
      }
    } catch (error: any) {
      console.error('Error fetching pending display names:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch pending display names');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: number, displayName: string) => {
    if (!confirm(`Approve display name "${displayName}"?`)) {
      return;
    }

    setProcessing(true);
    try {
      const response = await apiClient.put(`/api/v1/admin/display-names/${userId}/review`, {
        action: 'approve',
      });

      if (response.data.success) {
        toast.success(`Display name "${displayName}" approved!`);
        setPending(prev => prev.filter(item => item.userId !== userId));
      } else {
        toast.error(response.data.error || 'Failed to approve');
      }
    } catch (error: any) {
      console.error('Error approving display name:', error);
      toast.error(error.response?.data?.error || 'Failed to approve display name');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (userId: number, displayName: string) => {
    setSelectedUserId(userId);
    setSelectedDisplayName(displayName);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedUserId) return;

    setProcessing(true);
    try {
      const response = await apiClient.put(`/api/v1/admin/display-names/${selectedUserId}/review`, {
        action: 'reject',
        rejectionReason: rejectionReason.trim() || undefined,
      });

      if (response.data.success) {
        toast.success(`Display name "${selectedDisplayName}" rejected`);
        setPending(prev => prev.filter(item => item.userId !== selectedUserId));
        setRejectDialogOpen(false);
      } else {
        toast.error(response.data.error || 'Failed to reject');
      }
    } catch (error: any) {
      console.error('Error rejecting display name:', error);
      toast.error(error.response?.data?.error || 'Failed to reject display name');
    } finally {
      setProcessing(false);
    }
  };

  // Filter by search query
  const filteredPending = pending.filter(item =>
    item.legalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Display Name Review Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve host display name submissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={`${pending.length} Pending`}
            color="warning"
            icon={<BadgeIcon />}
          />
          <IconButton onClick={fetchPendingDisplayNames} disabled={loading}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name, display name, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 600 }}
        />
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Legal Name</TableCell>
                <TableCell>Requested Display Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Listings</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPending.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'No results found' : 'No pending display name reviews'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPending.map((item) => (
                  <TableRow key={item.userId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" color="action" />
                        {item.legalName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>
                        {item.displayName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {item.phoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.listingCount} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(item.requestedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Approve">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircle />}
                            onClick={() => handleApprove(item.userId, item.displayName)}
                            disabled={processing}
                          >
                            Approve
                          </Button>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => openRejectDialog(item.userId, item.displayName)}
                            disabled={processing}
                          >
                            Reject
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Display Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Rejecting display name: <strong>"{selectedDisplayName}"</strong>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason (Optional)"
            placeholder="e.g., Too similar to another host's name, Contains inappropriate content..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={processing}>
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisplayNameReviewPage;
